import type { MatchedLine, MatchSummary } from "../api/purchaseMatching";

export const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";

export type MatchBlockingReasonKind =
  | "ready"
  | "notMatched"
  | "quantityMismatch"
  | "waitingForReceipt"
  | "receiptRefresh"
  | "other";

export type MatchedOrderGroup = {
  orderLineId: string;
  order: MatchedLine;
  receipts: MatchedLine[];
};

export function isReceiptLevelMatch(match: MatchedLine): boolean {
  return Boolean(match.receiptLineId) && match.receiptLineId !== EMPTY_GUID;
}

export function groupMatchedLines(matches: MatchedLine[]): MatchedOrderGroup[] {
  const groups = new Map<
    string,
    { order?: MatchedLine; receipts: MatchedLine[] }
  >();

  for (const match of matches) {
    const current = groups.get(match.orderLineId) ?? { receipts: [] };

    if (isReceiptLevelMatch(match)) {
      current.receipts.push(match);
    } else {
      current.order = match;
    }

    groups.set(match.orderLineId, current);
  }

  return Array.from(groups.entries())
    .map(([orderLineId, group]) => ({
      orderLineId,
      order: group.order ?? group.receipts[0],
      receipts: [...group.receipts].sort((a, b) => {
        const receiptNoCompare = (a.receiptNo || "").localeCompare(
          b.receiptNo || "",
          "pl"
        );
        if (receiptNoCompare !== 0) return receiptNoCompare;
        return (a.receiptLineNo || 0) - (b.receiptLineNo || 0);
      }),
    }))
    .filter((group): group is MatchedOrderGroup => Boolean(group.order))
    .sort((a, b) => {
      const orderNoCompare = (a.order.orderNo || "").localeCompare(
        b.order.orderNo || "",
        "pl"
      );
      if (orderNoCompare !== 0) return orderNoCompare;
      return (a.order.orderLineNo || 0) - (b.order.orderLineNo || 0);
    });
}

export function classifyBlockingReason(
  summary: MatchSummary | null | undefined
): MatchBlockingReasonKind {
  if (!summary) return "other";
  if (summary.ready) return "ready";
  if (!summary.matched) return "notMatched";

  const reason = (summary.blockingReason || "").toLocaleLowerCase("pl-PL");

  if (reason.includes("suma ilości do zafakturowania")) {
    return "quantityMismatch";
  }

  if (reason.includes("nie ma jeszcze zaksięgowanego przyjęcia")) {
    return "waitingForReceipt";
  }

  if (
    reason.includes("istnieją przyjęcia") &&
    reason.includes("odśwież dopasowanie")
  ) {
    return "receiptRefresh";
  }

  // Fallback for unexpected BC wording: a non-zero difference still means
  // the quantities cannot pass the readiness check. Known receipt-specific
  // reasons above intentionally take precedence.
  if (Math.abs(summary.difference || 0) > 0.0000001) {
    return "quantityMismatch";
  }

  return "other";
}
